import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive } from "@/pages/Senders";
import { useAuth } from "@/lib/auth";
import { Trash, Plus } from "@phosphor-icons/react";
import AiSettingsPanel from "@/pages/AiSettingsPanel";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const load = async () => {
    const r = await api.get("/settings");
    setS(r.data);
    try {
      const u = await api.get("/users");
      setUsers(u.data.users);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    if (!newUser.email || !newUser.password) {
      toast.error("email and password required");
      return;
    }
    try {
      await api.post("/users", newUser);
      toast.success("User created");
      setNewUser({ email: "", name: "", password: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const deleteUser = async (id, email) => {
    if (!confirm(`Delete user ${email}?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const changeMyPassword = async () => {
    if (!pw.current_password || !pw.new_password) {
      toast.error("current and new password required");
      return;
    }
    try {
      await api.patch(`/users/${user.id}`, {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Change failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      // Only send api keys if the user actually typed one (they come back
      // masked/empty from the server, so sending them would wipe the stored key).
      if (!payload.deepseek_api_key) delete payload.deepseek_api_key;
      if (!payload.findclix_api_key) delete payload.findclix_api_key;
      if (!payload.openai_api_key) delete payload.openai_api_key;
      await api.patch("/settings", payload);
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/content/ai/test-connection", {
        api_key: s.deepseek_api_key, base_url: s.deepseek_base_url, model: s.deepseek_model,
      });
      r.data.success ? toast.success("AI connection OK") : toast.error(r.data.error);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  if (!s) return <div className="p-8 font-mono text-[#71717A]">[ LOADING... ]</div>;

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="micro mb-1">System</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Settings</h1>
        </div>
        <BtnPrimary onClick={save} testid="settings-save" disabled={saving}>Save changes</BtnPrimary>
      </div>

      <div className="space-y-8">
        <Section title="Campaign defaults">
          <div className="grid grid-cols-2 gap-4">
            <NumField label="Emails per mailbox" v={s.emails_per_mailbox} on={(v) => setS({ ...s, emails_per_mailbox: v })} tid="set-epm" />
            <NumField label="Batch size" v={s.batch_size} on={(v) => setS({ ...s, batch_size: v })} tid="set-batch" />
            <NumField label="Pause minimum (sec)" v={s.pause_minimum_seconds} on={(v) => setS({ ...s, pause_minimum_seconds: v })} tid="set-pmin" />
            <NumField label="Pause maximum (sec)" v={s.pause_maximum_seconds} on={(v) => setS({ ...s, pause_maximum_seconds: v })} tid="set-pmax" />
            <NumField label="Worker count" v={s.worker_count} on={(v) => setS({ ...s, worker_count: v })} tid="set-workers" />
            <NumField label="Retry count" v={s.retry_count} on={(v) => setS({ ...s, retry_count: v })} tid="set-retries" />
            <NumField label="Proxy connect timeout (sec)" v={s.proxy_connection_timeout} on={(v) => setS({ ...s, proxy_connection_timeout: v })} tid="set-pto" />
            <NumField label="Proxy retry count" v={s.proxy_retry_count} on={(v) => setS({ ...s, proxy_retry_count: v })} tid="set-pretry" />
          </div>
        </Section>

        <Section title="Proxy enforcement (locked)">
          <div className="flex items-center gap-3">
            <span className="text-[#E1FF00] font-mono text-xs">ON</span>
            <span className="text-sm text-[#A1A1AA]">
              Require sender proxy for all sends. Direct connection is prohibited.
            </span>
          </div>
        </Section>

        <Section title="Sending hostname (EHLO)">
          <div className="flex items-center justify-between gap-6" data-testid="ehlo-toggle-row">
            <div className="max-w-xl">
              <div className="text-sm text-white mb-1">Inject a device hostname on each send</div>
              <div className="text-xs text-[#A1A1AA] leading-relaxed">
                ON presents a realistic rotated computer name (e.g. "Rohit-MacBook-Pro") on the SMTP
                greeting. OFF sends a neutral greeting with no device name, proxy IP or hostname, so
                only your real detected IP is recorded by the receiving server.
              </div>
            </div>
            <button
              type="button"
              data-testid="ehlo-toggle"
              onClick={() => setS({ ...s, ehlo_custom_enabled: s.ehlo_custom_enabled === false })}
              className={`relative w-14 h-7 flex-shrink-0 border transition-colors ${
                s.ehlo_custom_enabled !== false
                  ? "bg-[#E1FF00] border-[#E1FF00]"
                  : "bg-[#0A0A0A] border-[#2A2A2A]"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 transition-all ${
                  s.ehlo_custom_enabled !== false ? "left-8 bg-black" : "left-0.5 bg-[#71717A]"
                }`}
              />
            </button>
          </div>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-wider text-[#71717A]" data-testid="ehlo-state">
            {s.ehlo_custom_enabled !== false ? "State: custom hostname ON" : "State: OFF \u2014 neutral greeting, real IP only"}
          </div>
        </Section>

        <AiSettingsPanel />

        <Section title="Change my password">
          <div className="grid grid-cols-3 gap-4 items-end max-w-2xl">
            <label className="block">
              <span className="micro block mb-1.5">Current password</span>
              <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                data-testid="pw-current" autoComplete="current-password"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
            </label>
            <label className="block">
              <span className="micro block mb-1.5">New password</span>
              <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                data-testid="pw-new" autoComplete="new-password"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
            </label>
            <BtnPrimary onClick={changeMyPassword} testid="pw-submit">Update password</BtnPrimary>
          </div>
        </Section>

        <Section title="Users">
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] mb-4 overflow-x-auto">
            <table className="dtable w-full" data-testid="users-table">
              <thead>
                <tr><th>Email</th><th>Name</th><th>Role</th><th>Created</th><th></th></tr>
              </thead>
              <tbody>
                {users.length === 0 && <tr><td colSpan={5} className="text-center text-[#71717A] py-6 font-mono">[ none ]</td></tr>}
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-mono text-xs">{u.email}{u.id === user?.id && <span className="ml-2 text-[10px] text-[#E1FF00]">(you)</span>}</td>
                    <td>{u.name}</td>
                    <td className="font-mono text-[10px] uppercase text-[#A1A1AA]">{u.role}</td>
                    <td className="font-mono text-xs text-[#71717A]">{u.created_at?.slice(0, 19)}</td>
                    <td>
                      {u.id !== user?.id && (
                        <button onClick={() => deleteUser(u.id, u.email)} data-testid={`user-del-${u.id}`}
                          className="w-7 h-7 flex items-center justify-center border border-[#2A2A2A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-[#A1A1AA]">
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-4 gap-3 items-end max-w-4xl">
            <label className="block">
              <span className="micro block mb-1.5">Email</span>
              <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="new-user-email"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
            </label>
            <label className="block">
              <span className="micro block mb-1.5">Name (optional)</span>
              <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                data-testid="new-user-name"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
            </label>
            <label className="block">
              <span className="micro block mb-1.5">Password</span>
              <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                data-testid="new-user-password"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
            </label>
            <BtnPrimary onClick={createUser} testid="new-user-submit"><Plus size={12} weight="bold" /> Add admin</BtnPrimary>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[#2A2A2A] p-6">
      <div className="micro mb-4">{title}</div>
      {children}
    </div>
  );
}

function NumField({ label, v, on, tid }) {
  return (
    <label className="block">
      <span className="micro block mb-1.5">{label}</span>
      <input type="number" value={v} onChange={(e) => on(Number(e.target.value))} data-testid={tid}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
    </label>
  );
}
